import { useState, useEffect, useMemo } from 'react';
import { loadMultiCampaignData } from '../components/SavedViews/multiCampaignDataLoader';

/**
 * useMultiCampaignData Hook
 * 
 * Manages loading and caching of multi-campaign view data.
 * Reloads when the view ID changes OR when the view's scope (campaign list) changes.
 */
export function useMultiCampaignData({ activeView, sprints, loadFullSprint }) {
  const [multiCampaignData, setMultiCampaignData] = useState(null);
  const [isLoadingViewData, setIsLoadingViewData] = useState(false);
  const [loadedViewKey, setLoadedViewKey] = useState(null);

  // Extract scope for stable dependency tracking
  const scope = activeView?.query?.scope;
  const scopeString = Array.isArray(scope) ? scope.join(',') : scope;
  
  // Create a stable key that changes when view ID or scope changes
  // This ensures we reload when campaigns are added/removed from the view
  const viewKey = useMemo(() => {
    if (!activeView?.id) return null;
    // Create a key from view ID + scope
    // For 'all' scope, just use 'all'; for array scope, sort and join IDs
    const scopeKey = scope === 'all' 
      ? 'all' 
      : Array.isArray(scope) 
        ? [...scope].sort().join(',') 
        : '';
    const key = `${activeView.id}:${scopeKey}`;
    return key;
  }, [activeView?.id, scope, scopeString]);

  // Load multi-campaign data when activeView changes or scope changes
  useEffect(() => {
    if (!viewKey) {
      setMultiCampaignData(null);
      setLoadedViewKey(null);
      return;
    }

    // Skip if we already loaded this exact view configuration
    if (viewKey === loadedViewKey) {
      console.log('[useMultiCampaignData] View already loaded, skipping:', viewKey);
      return;
    }

    let cancelled = false;
    
    const loadData = async () => {
      console.log('[useMultiCampaignData] Loading view data for:', viewKey);
      setIsLoadingViewData(true);
      try {
        const data = await loadMultiCampaignData(activeView, sprints, loadFullSprint);
        if (!cancelled) {
          setMultiCampaignData(data);
          setLoadedViewKey(viewKey);
          console.log('[useMultiCampaignData] View data loaded successfully:', viewKey);
        }
      } catch (error) {
        console.error('[useMultiCampaignData] Failed to load multi-campaign data:', error);
        if (!cancelled) {
          setMultiCampaignData(null);
          setLoadedViewKey(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingViewData(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [viewKey, activeView, sprints, loadFullSprint, loadedViewKey]); // Reload when view ID or scope changes

  return {
    multiCampaignData,
    isLoadingViewData,
  };
}

export default useMultiCampaignData;
